import { UrgencyBar } from '@hyphengroup/cms-components/components/UrgencyBar';
import { ExclusiveOfferTypeEnum } from '@hyphengroup/cms-components/types/cmsEnums';
import { ReactNode } from 'react';
import { getActiveStandaloneOffers } from '../lib/helpers';
import { ApplyViewMode } from '../lib/types';
import { LegalText } from './LegalText/LegalText';
import { RewardCatalogue } from './RewardCatalogue/RewardCatalogue';
import { ApplyProgressStepper } from '$/(cms)/-cms/apply-v3/[[...slugs]]/components/ApplyProgressStepper';
import { ProductCard } from '$/(cms)/-cms/apply-v3/[[...slugs]]/components/ProductCard/ProductCard';
import { mapExclusiveOfferBadgeColorToUrgencyBarColor } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/urgency-bar-color.mapper';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { ApplyMode } from '$/(cms)/_modules/routing';

type Props = {
  mode: ApplyMode;
  viewMode: ApplyViewMode;
  product: CmsApplyRewardProduct;
  initialNow: number;
  // Modal view renders the auth section inline (between the Flash Deal banner
  // and the legal text) instead of a sticky footer. `undefined` in page view.
  authSlot?: ReactNode;
};

export function ApplyContent({
  mode,
  viewMode,
  product,
  initialNow,
  authSlot,
}: Props) {
  const isModal = viewMode === ApplyViewMode.Modal;

  const activeOffers = getActiveStandaloneOffers(product);
  const urgencyOffer = activeOffers.find(
    (offer) => offer.includeCountdownTimer && offer.period?.end_date,
  );
  const urgencyEndDate = urgencyOffer?.period?.end_date;
  /**
   * Display if there is an active Flash Deal offer
   */
  const showFlashDeal =
    urgencyOffer?.exclusive_offer_type === ExclusiveOfferTypeEnum.Flash;

  // Page M2/M3 keep the existing urgency behaviour; the modal (M1) surfaces the
  // Flash Deal banner, which is otherwise hidden for PDP mode.
  const showUrgency =
    urgencyEndDate != null &&
    (mode !== ApplyMode.PDP || (isModal && showFlashDeal));

  const showFlashDealUrgencyBar =
    showFlashDeal && (mode === ApplyMode.Campaign || isModal);

  /**
   * Reward catalogue: mode 2 / mode 3 only — stays hidden in the modal (M1).
   */
  const displayRewardCatalogue = mode !== ApplyMode.PDP;

  return (
    <>
      <ApplyProgressStepper />
      {product.catalogueProduct ? (
        <ProductCard
          product={product.catalogueProduct}
          trackingProduct={product}
          isModal={isModal}
        />
      ) : null}
      {showUrgency ? (
        <UrgencyBar
          endDate={urgencyEndDate}
          flashDealLabel={urgencyOffer?.flash_deal_label}
          initialNow={initialNow}
          showFlashDeal={showFlashDealUrgencyBar}
          highlight={mapExclusiveOfferBadgeColorToUrgencyBarColor(
            'orange',
            urgencyOffer?.badge_color,
          )}
        />
      ) : null}
      {displayRewardCatalogue && (
        <>
          {showFlashDeal && (
            <RewardCatalogue displayFlash={showFlashDeal} {...product} />
          )}
          <RewardCatalogue displayFlash={false} {...product} />
        </>
      )}
      {authSlot}
      {isModal && authSlot ? (
        // Modal (M1) only: full-bleed divider between the inline auth section
        // and the legal text (per design). The negative horizontal margin
        // cancels the content section's padding so the line reaches the modal
        // edges; the section's vertical gap provides the spacing above/below.
        <div
          aria-hidden
          className="-mx-spacing/padding/section-x border-t border-borders/default"
        />
      ) : null}
      <LegalText />
    </>
  );
}
